import { FC, useCallback, useState } from "react";

import useCart from "../useCart";

import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import Payment, { PaymentProps } from "modules/Payment";
import { PaymentContext } from "modules/Payment/paymentContext";

import CustomLink from "components/CustomLink";
import CustomCheckbox from "components/CustomCheckbox";
import Modal from "components/Modal";

import { CHAIN_IDS } from "enums/chainIds";

import { APP_ROUTES } from "config/routes";

import { PaymentDetail } from "services/store/PaymentDetail.interface";

interface PaymentModalProps {
  isOpen: boolean;
  allowedChainIds: CHAIN_IDS[];
  onClose: () => void;
  totalFiatPrice: number;
  paymentDetails: PaymentDetail[];
  isFree: boolean;
}

const PaymentModal: FC<
  PaymentModalProps &
    Pick<
      PaymentProps,
      "onBeforePayment" | "onAfterPayment" | "onCryptoPayment" | "onFiatPayment"
    > & { modalType?: "STORE" | "REDEEM" }
> = ({
  isOpen,
  allowedChainIds,
  onClose,
  totalFiatPrice,
  paymentDetails,
  isFree,
  onCryptoPayment,
  onFiatPayment,
  onAfterPayment,
  onBeforePayment,
  modalType = "STORE",
}) => {
  const { areFiatsAvailable, fiatCurrency } = useCart();
  const [isConsentChecked, setConsentChecked] = useState(false);

  const onConsentChange = useCallback((e) => {
    setConsentChecked(e.target.checked);
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      handleClose={onClose}
      title="Checkout"
      disableBackdropClick
    >
      <Box sx={{ maxWidth: 600, minWidth: { lg: 600 } }}>
        <PaymentContext.Provider
          value={{
            availableAmount: 0,
            buyDisabled: !isConsentChecked,
            bundleId: null,
            isFreeMint: isFree,
            buttonText: "Pay",
            modalType,
            chainIds: allowedChainIds,
            fiatPrice: totalFiatPrice,
            paymentDetails,
            uniqDiscount: null,
            fiatCurrency,
            mintChainId: null,
          }}
        >
          <Payment
            showQuantity={false}
            showPaymentMethods={!isFree}
            showCurrency={!isFree}
            showNetworks={!isFree}
            onCryptoPayment={onCryptoPayment}
            onFiatPayment={onFiatPayment}
            onAfterPayment={onAfterPayment}
            onBeforePayment={onBeforePayment}
            disableFiatPayment={!areFiatsAvailable}
          >
            <>
              <FormControlLabel
                value="end"
                checked={isConsentChecked}
                control={<CustomCheckbox />}
                sx={{ mt: 2, mb: 1 }}
                label={
                  <Typography
                    component="span"
                    variant="subtitle1"
                    fontWeight="light"
                  >
                    I have read and accept{" "}
                    <CustomLink
                      href={APP_ROUTES.TERMS}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms & Conditions
                    </CustomLink>
                    {" and "}
                    <CustomLink
                      href={APP_ROUTES.PRIVACY}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </CustomLink>
                    *
                  </Typography>
                }
                labelPlacement="end"
                onChange={onConsentChange}
              />
              <Typography variant="body2">
                *Required consents / fields
              </Typography>
            </>
          </Payment>
        </PaymentContext.Provider>
      </Box>
    </Modal>
  );
};

export default PaymentModal;
